import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { BootScreen, StatusGlyph } from '../components/StateViews.jsx';

export default function Login() {
  const { status, signIn, signedOutReason } = useAuth();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const userField = useRef(null);

  useEffect(() => {
    if (status === 'anon') userField.current?.focus();
  }, [status]);

  if (status === 'checking') return <BootScreen />;
  if (status === 'authed') {
    const from = location.state?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    if (!username.trim() || !password) {
      setError('Enter both a username and a password.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await signIn(username.trim(), password);
    } catch (err) {
      setError(err.message || 'Sign-in failed.');
      setPassword('');
      setSubmitting(false);
    }
  }

  return (
    <div className="presto-app login">
      <form className="login__card" onSubmit={handleSubmit} noValidate>
        <div className="login__brand">
          <span className="brand__mark" aria-hidden="true" />
          <span className="brand__text">
            <strong>Presto Studio</strong>
            <span>Hosting preview</span>
          </span>
        </div>

        <h1 className="login__title">Welcome back</h1>
        <p className="login__lede">
          Studio accounts are managed by your administrator. There is no self-service signup.
        </p>

        {signedOutReason && !error ? (
          <p className="notice" role="status">
            {signedOutReason}
          </p>
        ) : null}

        {error ? (
          <p className="notice notice--error" role="alert">
            <StatusGlyph tone="critical" shape="diamond" />
            {error}
          </p>
        ) : null}

        <label className="field" htmlFor="username">
          <span className="field__label">Username</span>
          <input
            id="username"
            name="username"
            ref={userField}
            className="field__input mono"
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={submitting}
            aria-invalid={error ? 'true' : undefined}
          />
        </label>

        <label className="field" htmlFor="password">
          <span className="field__label">Password</span>
          <input
            id="password"
            name="password"
            className="field__input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={submitting}
            aria-invalid={error ? 'true' : undefined}
          />
        </label>

        <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="login__foot">
          Five failed attempts per username locks sign-in for 15 minutes.
        </p>
      </form>
    </div>
  );
}
